import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Alert,
} from '@mui/material';

interface CreatePermissionGroupDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (groupData: any) => void;
  availablePermissions: Array<{ id: string; name: string; resource: string; action: string }>;
}

const CreatePermissionGroupDialog: React.FC<CreatePermissionGroupDialogProps> = ({
  open,
  onClose,
  onSave,
  availablePermissions,
}) => {
  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  const handleSave = () => {
    const groupData = {
      name: groupName,
      description,
      permissions: selectedPermissions,
    };
    onSave(groupData);
    handleClose();
  };

  const handleClose = () => {
    setGroupName('');
    setDescription('');
    setSelectedPermissions([]);
    onClose();
  };

  const togglePermission = (permissionKey: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionKey)
        ? prev.filter((p) => p !== permissionKey)
        : [...prev, permissionKey]
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Create Permission Group
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Permission groups allow you to bundle related permissions together for easier role management.
          </Alert>

          <TextField
            fullWidth
            label="Group Name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            sx={{ mb: 3 }}
            required
          />

          <TextField
            fullWidth
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Select Permissions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose the permissions to include in this group
          </Typography>

          <List sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
            {availablePermissions.map((permission) => {
              const permissionKey = `${permission.resource}:${permission.action}`;
              const isSelected = selectedPermissions.includes(permissionKey);
              return (
                <ListItem
                  key={permission.id}
                  button
                  onClick={() => togglePermission(permissionKey)}
                  sx={{
                    backgroundColor: isSelected ? 'action.selected' : 'transparent',
                  }}
                >
                  <ListItemIcon>
                    <Checkbox checked={isSelected} />
                  </ListItemIcon>
                  <ListItemText
                    primary={permission.name}
                    secondary={
                      <Box display="flex" gap={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                        <Chip label={permission.resource} size="small" variant="outlined" />
                        <Chip label={permission.action} size="small" variant="outlined" />
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>

          {selectedPermissions.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                SELECTED PERMISSIONS ({selectedPermissions.length})
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {selectedPermissions.map((perm) => (
                  <Chip
                    key={perm}
                    label={perm}
                    size="small"
                    variant="outlined"
                    onDelete={() => togglePermission(perm)}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!groupName || selectedPermissions.length === 0}
        >
          Create Group
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePermissionGroupDialog;

